/**
 * Route paths for mops-web. Admin tool paths are added incrementally as each
 * tool migrates from `@orn/web` — see the batched migration strategy under
 * ORN-526.
 */
export const PATHS = {
  HOME: '/',
  ALPHA_ALLOW_LIST: '/alpha-allow-list',
  /** Assistant tool — parent route wrapping the memory + sessions sub-tabs. */
  ASSISTANT: '/assistant',
  /** Memory sub-tab of the Assistant tool — lists memories and supports deletion. */
  ASSISTANT_MEMORY: '/assistant/memory',
  /** Sessions sub-tab — left-list/right-detail inspector with hard-delete of one or all threads for a user. */
  ASSISTANT_SESSIONS: '/assistant/sessions',
  /** Feedback sub-tab — global newest-first table of `/issue` submissions, each row deep-linking into Sessions. */
  ASSISTANT_FEEDBACK: '/assistant/feedback',
  BANK_CONNECTIONS: '/bank-connections',
  BILL_MANAGEMENT: '/bill-management',
  DELETE_ACCOUNT: '/delete-account',
  FEATURE_GATES: '/feature-gates',
  /** Detail route for editing an existing gate by id. */
  FEATURE_GATE_EDIT: '/feature-gates/:id',
  /** New-gate creation form — flat path, kept in sync with the list view. */
  FEATURE_GATE_NEW: '/feature-gates/new',
  IMPERSONATE_USER: '/impersonate-user',
  OBJECT_INSPECTOR: '/object-inspector',
  PROCESSOR_STATUSES: '/processor-statuses',
  RELEASES: '/releases',
  TRANSACTION_ANOMALY_FEEDBACK: '/transaction-anomaly-feedback',
  /** Pattern for the per-user detail route — consumed by the React Router definition. */
  TRANSACTION_ANOMALY_FEEDBACK_FOR_USER: '/transaction-anomaly-feedback/:userId',
} as const;

export type PathKey = keyof typeof PATHS;
export type PathValue = (typeof PATHS)[PathKey];

/** Builds the URL for the per-user transaction anomaly feedback detail scene. */
export function transactionAnomalyFeedbackForUserPath(userId: string): string {
  return `/transaction-anomaly-feedback/${userId}`;
}

/** Builds the URL for editing a feature gate by id. */
export function featureGateEditPath(id: string): string {
  return `/feature-gates/${id}`;
}

/** Builds a link to the Feedback tab anchored to (scroll + highlight) one row. */
export function assistantFeedbackAnchorPath(feedbackId: string): string {
  return `${PATHS.ASSISTANT_FEEDBACK}?feedbackId=${encodeURIComponent(feedbackId)}`;
}

/**
 * Builds a deep link into the Sessions inspector, carrying user/family/thread
 * so the inspector auto-selects the thread. Pass `turnId` to additionally
 * scroll to (and highlight) a specific turn; omit it to land on the thread
 * alone (the memory-provenance link has no turn to target).
 */
export function assistantSessionDeepLinkPath(params: {
  userId: string;
  familyId: string;
  threadId: string;
  turnId?: string;
}): string {
  const { turnId, ...rest } = params;
  const query = new URLSearchParams(turnId ? { ...rest, turnId } : rest).toString();
  return `${PATHS.ASSISTANT_SESSIONS}?${query}`;
}
